import { OidcClient, OidcProvider } from '@axa-fr/react-oidc';
import type { Client } from '@sentry/core';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider } from 'jotai';
import {
  type PropsWithChildren,
  StrictMode,
  Suspense,
  startTransition,
  useMemo,
  useRef,
} from 'react';
import * as ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { initReactI18next, useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router';
import { AuthLoading } from './components/auth-loading/AuthLoading';
import { MaintenanceGate } from './components/maintenance-mode/MaintenanceGate';
import { PageHead } from './components/page-head/PageHead';
import { Translations } from './i18n/translations';
import { oidcConfig } from './oidcConfig';
import { AuthError } from './pages/auth-error/AuthError';
import { createRouter } from './router';
import { store } from './stores';
import { getBatchLink, trpc } from './trpc';
import { DEFAULT_LOCALE, initI18n, Locale } from './utils/i18n';
import './index.css';
import i18next from 'i18next';
import { CookiesProvider } from 'react-cookie';
import { VITE_MOCK_CMS, VITE_SENTRY_DSN } from '#pie/env';

const localLangKey = 'i18nextLng';

// Guards against a persistently failing OIDC session (e.g. the service
// worker never syncing tokens) reloading the tab forever, which was hammering
// OidcServiceWorker.js/OidcTrustedDomains.js with millions of requests.
const SESSION_LOST_RELOAD_STORAGE_KEY = 'pie.session_lost_reload_count';
const SESSION_LOST_RELOAD_MAX_ATTEMPTS = 2;

const getSessionLostReloadCount = (): number => {
  try {
    return Number.parseInt(
      sessionStorage.getItem(SESSION_LOST_RELOAD_STORAGE_KEY) ?? '0',
      10,
    );
  } catch {
    return SESSION_LOST_RELOAD_MAX_ATTEMPTS;
  }
};

const clearSessionLostReloadCount = () => {
  try {
    sessionStorage.removeItem(SESSION_LOST_RELOAD_STORAGE_KEY);
  } catch {
    // ignore
  }
};

let lastSessionLostEvent: { name: string; data: unknown } | undefined;

const handleSessionLost = () => {
  const attempts = getSessionLostReloadCount();
  if (attempts >= SESSION_LOST_RELOAD_MAX_ATTEMPTS) {
    Sentry.captureMessage('OIDC session lost: reload budget exhausted', {
      level: 'error',
      extra: { attempts, lastEvent: lastSessionLostEvent },
    });
    return;
  }
  try {
    sessionStorage.setItem(
      SESSION_LOST_RELOAD_STORAGE_KEY,
      String(attempts + 1),
    );
  } catch {
    // ignore
  }
  window.location.reload();
};

const initSentry = (
  options: Sentry.BrowserOptions,
  maskAllText = true,
): Client | undefined =>
  Sentry.init({
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText }),
    ],
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    tracesSampleRate: 1.0,
    ...options,
  });

export { captureException } from '@sentry/react';

(async () => {
  VITE_SENTRY_DSN &&
    initSentry({
      dsn: VITE_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.captureConsoleIntegration(),
      ],
    });

  if (VITE_MOCK_CMS) {
    const worker = (await import('./mocks/browser')).worker;
    await worker.start({ onUnhandledRequest: 'bypass' });
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        retry: 1,
      },
    },
  });

  initI18n(
    (language, namespace) =>
      import(`./i18n/locales/${language}/${namespace}.json`),
    (localStorage.getItem(localLangKey) as Locale | undefined) || Locale.Dutch,
    'common',
  );

  i18next.use(initReactI18next).on('languageChanged', (lang) => {
    localStorage.setItem(localLangKey, lang);
  });

  function TRPCProvider({ children }: PropsWithChildren) {
    const {
      i18n: { language },
    } = useTranslation();

    const locale = Object.values(Locale).includes(language as Locale)
      ? (language as Locale)
      : DEFAULT_LOCALE;

    const localeRef = useRef(locale);
    localeRef.current = locale;

    const trpcClient = useMemo(
      () =>
        trpc.createClient({
          links: [getBatchLink({ localeRef, urlPathname: '' })],
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    return (
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          {children}
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </trpc.Provider>
    );
  }

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );

  startTransition(() => {
    root.render(
      <StrictMode>
        <CookiesProvider>
          <Suspense fallback={<div>Loading...</div>}>
            <HelmetProvider>
              <Translations>
                <TRPCProvider>
                  <OidcProvider
                    onEvent={(_, name, data) => {
                      if (name === OidcClient.eventNames.token_acquired) {
                        clearSessionLostReloadCount();
                        if (
                          data?.idTokenPayload?.acr?.includes('eherkenning')
                        ) {
                          window.location.assign('/auth-error?eh=true');
                        }
                      } else if (
                        name ===
                          OidcClient.eventNames.refreshTokensAsync_error ||
                        name === OidcClient.eventNames.syncTokensAsync_error
                      ) {
                        lastSessionLostEvent = { name, data };
                        Sentry.captureMessage(`OIDC ${name}`, {
                          level: 'warning',
                          extra: { data },
                        });
                      }
                    }}
                    configuration={oidcConfig}
                    loadingComponent={() => null}
                    authenticatingComponent={AuthLoading}
                    callbackSuccessComponent={() => null}
                    authenticatingErrorComponent={AuthError}
                    sessionLostComponent={() => {
                      handleSessionLost();
                      return null;
                    }}
                  >
                    <Provider store={store}>
                      <PageHead />
                      <MaintenanceGate>
                        <RouterProvider router={createRouter()} />
                      </MaintenanceGate>
                    </Provider>
                  </OidcProvider>
                </TRPCProvider>
              </Translations>
            </HelmetProvider>
          </Suspense>
        </CookiesProvider>
      </StrictMode>,
    );
  });
})();
